import { useState, useEffect, useCallback } from 'react'
import { useAuthStore } from '../stores/authStore'
import { usePortalAuthStore } from '../stores/portalAuthStore'
import { refreshSessionTokens } from '../services/api'

interface SessionTimeoutState {
  minutesRemaining: number | null
  isExpiringSoon: boolean
  isExpiryCritical: boolean
  isExpired: boolean
}

function decodeJwtExp(token: string): number | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    // JWT payloads are base64url, not base64: translate the alphabet and
    // restore padding before atob, which rejects '-' and '_' outright.
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
    const payload = JSON.parse(atob(padded))
    return typeof payload.exp === 'number' ? payload.exp : null
  } catch {
    return null
  }
}

export function useSessionTimeout(): SessionTimeoutState & { refresh: () => void } {
  const accessToken = useAuthStore((s) => s.accessToken)
  const logout = useAuthStore((s) => s.logout)

  const [state, setState] = useState<SessionTimeoutState>({
    minutesRemaining: null,
    isExpiringSoon: false,
    isExpiryCritical: false,
    isExpired: false,
  })

  const refresh = useCallback(async () => {
    const refreshToken = localStorage.getItem('refresh_token')
    if (!refreshToken) return

    try {
      // Shared single-flight refresh: persists the ROTATED refresh token
      // (keeping the predecessor would kill the session at next expiry)
      // and coalesces with any interceptor refresh already in flight.
      await refreshSessionTokens(refreshToken)
    } catch {
      // Refresh failed silently -- will be caught by the next tick
    }
  }, [])

  useEffect(() => {
    if (!accessToken) {
      setState({
        minutesRemaining: null,
        isExpiringSoon: false,
        isExpiryCritical: false,
        isExpired: false,
      })
      return
    }

    const checkExpiry = () => {
      const exp = decodeJwtExp(accessToken)
      if (exp === null) return

      const nowSeconds = Math.floor(Date.now() / 1000)
      const remainingSeconds = exp - nowSeconds
      const remainingMinutes = Math.max(0, Math.ceil(remainingSeconds / 60))

      if (remainingSeconds <= 0) {
        setState({
          minutesRemaining: 0,
          isExpiringSoon: true,
          isExpiryCritical: true,
          isExpired: true,
        })
        // `logout()` clears the zustand store only. The localStorage token
        // pair survives it, so a /login reload re-authenticates via
        // checkAuth + refresh AFTER the supposed logout. Tear the persisted
        // session down fully — staff tokens and any portal session sharing
        // this browser (shared-kiosk hygiene).
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        // S5-106: logout() clears the portal store only in memory — without
        // removing the persisted key the /login reload rehydrates the portal
        // session (shared-kiosk leak). Order matches the portal-401 path in
        // api.ts: logout() first, then remove (never re-persisted).
        usePortalAuthStore.getState().logout()
        localStorage.removeItem('vynaemr-portal-auth')
        logout()
        window.location.href = '/login'
        return
      }

      setState({
        minutesRemaining: remainingMinutes,
        isExpiringSoon: remainingMinutes <= 5,
        isExpiryCritical: remainingMinutes <= 1,
        isExpired: false,
      })
    }

    checkExpiry()
    const interval = setInterval(checkExpiry, 15_000) // check every 15s
    return () => clearInterval(interval)
  }, [accessToken, logout])

  return { ...state, refresh }
}
